import React, { useState, useEffect } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { theme } from '../styles/theme';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';
import { VideoTestimonialCarousel } from './VideoTestimonialCarousel';
import { VideoModal } from './VideoModal';
import { PhoneModal } from './PhoneModal';

// Global styles for viewport units and smooth transitions
const GlobalViewportStyles = createGlobalStyle`
  :root {
    --vh: 1vh;
    --vw: 1vw;
  }
  
  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  /* Smooth transitions for responsive changes */
  @media (prefers-reduced-motion: no-preference) {
    * {
      transition: font-size 0.3s ease, padding 0.3s ease, margin 0.3s ease, gap 0.3s ease;
    }
  }
`;

// Define viewport categories for responsive design with better breakpoints
const getViewportCategory = (vw: number, vh: number) => {
  // Use viewport width as primary factor for consistency
  if (vw < 375) return 'mobile-small'; // Very small phones (iPhone SE, etc)
  if (vw < 768) return 'mobile'; // Regular phones
  if (vw < 1024) return 'tablet'; // Tablets
  if (vw < 1920) return 'desktop'; // Desktop
  return 'tv'; // Large screens/TVs (1920px+)
};

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.9; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Main hero wrapper
const HeroWrapper = styled.section<{ $vh: number }>`
  background-image: url('/650f1c8c6cf62f0b696c3d20_Lake2.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: ${props => props.$vh}px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, 
      rgba(26, 35, 50, 0.95) 0%, 
      rgba(44, 62, 80, 0.95) 50%, 
      rgba(26, 35, 50, 0.95) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(ellipse at top, rgba(255, 215, 0, 0.05) 0%, transparent 50%),
      radial-gradient(ellipse at bottom, rgba(16, 163, 127, 0.05) 0%, transparent 50%);
    pointer-events: none;
    z-index: 2;
  }
`;

const HeroContent = styled.div<{ $category: string }>`
  max-width: ${props => {
    switch(props.$category) {
      case 'tv': return '2000px';
      case 'desktop': return '1400px';
      case 'tablet': return '100%';
      default: return '100%';
    }
  }};
  margin: 0 auto;
  position: relative;
  z-index: 3;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: ${props => {
    switch(props.$category) {
      case 'tv': return 'clamp(15px, 1.5vh, 25px) clamp(30px, 2vw, 50px)';
      case 'desktop': return 'clamp(12px, 1.3vh, 20px) clamp(25px, 2vw, 40px)';
      case 'tablet': return 'clamp(8px, 1.2vh, 15px) clamp(15px, 2vw, 25px)';
      case 'mobile': return '8px 12px';
      default: return '5px 8px';
    }
  }};
`;

// Premium Alert - Responsive
const PremiumAlert = styled.div<{ $category: string; $vh: number }>`
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 100, 0, 0.15) 100%);
  border: 2px solid rgba(255, 215, 0, 0.4);
  color: white;
  text-align: center;
  border-radius: ${props => props.$category === 'mobile' || props.$category === 'mobile-small' ? '16px' : '50px'};
  backdrop-filter: blur(20px);
  box-shadow: 0 10px 30px rgba(255, 215, 0, 0.2);
  width: 95%;
  max-width: ${props => props.$category === 'mobile' || props.$category === 'mobile-small' ? '350px' : '900px'};
  margin: 0 auto;
  margin-bottom: ${props => {
    switch(props.$category) {
      case 'tv': return 'clamp(15px, 1.5vh, 25px)';
      case 'desktop': return 'clamp(12px, 1.3vh, 20px)';
      case 'tablet': return 'clamp(10px, 1.2vh, 15px)';
      case 'mobile': return '8px';
      default: return '5px';
    }
  }};
  padding: ${props => {
    switch(props.$category) {
      case 'tv': return 'clamp(18px, 1.5vh, 25px) clamp(35px, 2vw, 50px)';
      case 'desktop': return 'clamp(12px, 1.3vh, 18px) clamp(25px, 2vw, 35px)';
      case 'tablet': return 'clamp(10px, 1.5vh, 15px) clamp(20px, 2.5vw, 30px)';
      case 'mobile': return '15px 20px';
      default: return '12px 15px';
    }
  }};
  font-size: ${props => {
    switch(props.$category) {
      case 'tv': return 'clamp(18px, 1.5vw, 24px)';
      case 'desktop': return 'clamp(15px, 1.3vw, 18px)';
      case 'tablet': return 'clamp(13px, 1.8vw, 16px)';
      case 'mobile': return '14px';
      default: return '12px';
    }
  }};
  line-height: 1.5;
  animation: ${props => props.$vh > 500 ? fadeIn : 'none'} 0.6s ease-out;
  flex-shrink: 0;
  
  strong {
    color: #ffd700;
    font-weight: 800;
    text-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
  }
`;

// Slider Container
const SliderContainer = styled.div<{ $category: string }>`
  position: relative;
  overflow: hidden;
  background: transparent;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  ${props => (props.$category === 'mobile' || props.$category === 'mobile-small') && `
    height: 100%;
  `}
`;

const SliderTrack = styled.div<{ $currentSlide: number }>`
  display: flex;
  transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform: translateX(-${props => props.$currentSlide * 100}%);
  height: 100%;
`;

// Slide Container with smart overflow and responsive padding
const Slide = styled.div<{ $category: string }>`
  min-width: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${props => 
    props.$category === 'mobile-small' || props.$category === 'mobile' ? 'space-between' : 'center'
  };
  text-align: center;
  box-sizing: border-box;
  overflow-y: ${props => 
    props.$category === 'mobile-small' || props.$category === 'mobile' ? 'hidden' : 'auto'
  };
  overflow-x: hidden;
  background: transparent;
  padding: ${props => {
    switch(props.$category) {
      case 'tv': return 'clamp(30px, 3vh, 50px) clamp(30px, 2vw, 50px)';
      case 'desktop': return 'clamp(20px, 2.5vh, 35px) clamp(20px, 2vw, 35px)';
      case 'tablet': return 'clamp(15px, 2vh, 25px) clamp(15px, 2vw, 25px)';
      case 'mobile': return '15px 15px';
      default: return '12px 12px';
    }
  }};
  gap: ${props => {
    switch(props.$category) {
      case 'tv': return 'clamp(20px, 2vh, 30px)';
      case 'desktop': return 'clamp(15px, 1.8vh, 25px)';
      case 'tablet': return 'clamp(12px, 1.5vh, 18px)';
      case 'mobile': return '10px';
      default: return '8px';
    }
  }};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 215, 0, 0.3);
    border-radius: 3px;
  }
`;

// Dynamic Title
const SlideTitle = styled.h1<{ $category: string }>`
  font-size: ${props => {
    switch(props.$category) {
      case 'tv': return 'clamp(60px, 5vw, 90px)';
      case 'desktop': return 'clamp(42px, 4vw, 56px)';
      case 'tablet': return 'clamp(24px, 3.5vw, 32px)';
      case 'mobile': return 'clamp(24px, 6vw, 28px)';
      default: return 'clamp(20px, 5.5vw, 24px)';
    }
  }};
  font-weight: 900;
  color: white;
  margin: 0;
  line-height: 1.1;
  letter-spacing: -0.02em;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  animation: ${fadeIn} 0.6s ease-out;
  flex-shrink: 0;
  
  .gold {
    color: #ffd700;
    display: inline-block;
    animation: ${pulse} 3s ease-in-out infinite;
    text-shadow: 0 0 30px rgba(255, 215, 0, 0.6);
  }
`;

// Dynamic Subtitle
const SlideSubtitle = styled.p<{ $category: string }>`
  font-size: ${props => {
    switch(props.$category) {
      case 'tv': return 'clamp(28px, 2.2vw, 36px)';
      case 'desktop': return 'clamp(20px, 1.8vw, 26px)';
      case 'tablet': return 'clamp(14px, 2vw, 18px)';
      case 'mobile': return 'clamp(15px, 4vw, 17px)';
      default: return 'clamp(13px, 3.8vw, 15px)';
    }
  }};
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  line-height: 1.4;
  max-width: 800px;
  font-weight: 400;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
`;

// Question Card - Responsive
const QuestionCard = styled.div<{ $category: string }>`
  background: linear-gradient(135deg, rgba(16, 163, 127, 0.15) 0%, rgba(16, 163, 127, 0.08) 100%);
  border: 3px solid rgba(16, 163, 127, 0.4);
  border-radius: ${props => props.$category === 'mobile' || props.$category === 'mobile-small' ? '16px' : '20px'};
  max-width: 900px;
  margin: 0 auto;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  width: 95%;
  box-sizing: border-box;
  padding: ${props => {
    switch(props.$category) {
      case 'tv': return '40px';
      case 'desktop': return '30px';
      case 'tablet': return '25px';
      case 'mobile': return '16px';
      default: return '14px';
    }
  }};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(16, 163, 127, 0.3);
    border-color: rgba(16, 163, 127, 0.6);
  }
  
  h3 {
    font-size: ${props => {
      switch(props.$category) {
        case 'tv': return '32px';
        case 'desktop': return '24px';
        case 'tablet': return '20px';
        case 'mobile': return '16px';
        default: return '14px';
      }
    }};
    color: #10a37f;
    margin: 0 0 10px 0;
    font-weight: 800;
    text-align: center;
    text-shadow: 0 2px 10px rgba(16, 163, 127, 0.3);
  }
  
  p {
    font-size: ${props => {
      switch(props.$category) {
        case 'tv': return '24px';
        case 'desktop': return '18px';
        case 'tablet': return '16px';
        case 'mobile': return '13px';
        default: return '12px';
      }
    }};
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.95);
    text-align: left;
    margin: 0;
  }
`;

// Trust Grid - Responsive columns
const TrustGrid = styled.div<{ $category: string }>`
  display: grid;
  grid-template-columns: ${props => {
    switch(props.$category) {
      case 'tv': 
      case 'desktop': return 'repeat(4, 1fr)';
      case 'tablet': return 'repeat(auto-fit, minmax(120px, 1fr))';
      case 'mobile':
      case 'mobile-small': return 'repeat(2, 1fr)';
      default: return 'repeat(2, 1fr)';
    }
  }};
  gap: ${props => {
    switch(props.$category) {
      case 'tv': return 'clamp(15px, 1.5vw, 25px)';
      case 'desktop': return 'clamp(12px, 1.2vw, 20px)';
      case 'tablet': return 'clamp(10px, 1.5vw, 15px)';
      case 'mobile': return 'clamp(8px, 2vw, 12px)';
      default: return 'clamp(6px, 1.8vw, 10px)';
    }
  }};
  max-width: 900px;
  margin: 0 auto;
  width: 95%;
  box-sizing: border-box;
`;

// Trust Card - Responsive
const TrustCard = styled.div<{ $category: string }>`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border: 2px solid rgba(255, 215, 0, 0.3);
  border-radius: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '12px' : '20px'};
  text-align: center;
  transition: all 0.3s;
  padding: ${props => {
    switch(props.$category) {
      case 'tv': return '25px 15px';
      case 'desktop': return '20px 10px';
      case 'tablet': return '15px 8px';
      case 'mobile': return '12px 6px';
      default: return '8px 4px';
    }
  }};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(255, 215, 0, 0.2);
    border-color: rgba(255, 215, 0, 0.5);
  }
  
  .icon {
    font-size: ${props => {
      switch(props.$category) {
        case 'tv': return '48px';
        case 'desktop': return '36px';
        case 'tablet': return '28px';
        case 'mobile': return '24px';
        default: return '20px';
      }
    }};
    margin-bottom: 8px;
    filter: drop-shadow(0 4px 10px rgba(255, 215, 0, 0.3));
  }
  
  .number {
    font-size: ${props => {
      switch(props.$category) {
        case 'tv': return '42px';
        case 'desktop': return '32px';
        case 'tablet': return '24px';
        case 'mobile': return '20px';
        default: return '18px';
      }
    }};
    font-weight: 900;
    color: #ffd700;
    margin-bottom: 5px;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.4);
  }
  
  .label {
    font-size: ${props => {
      switch(props.$category) {
        case 'tv': return '14px';
        case 'desktop': return '12px';
        case 'tablet': return '11px';
        case 'mobile': return '10px';
        default: return '9px';
      }
    }};
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.8);
    letter-spacing: 1px;
    font-weight: 600;
  }
`;

// Expert Image Container
const ExpertImageContainer = styled.div<{ $category: string }>`
  position: relative;
  animation: ${props => props.$category !== 'mobile-small' && props.$category !== 'mobile' ? float : 'none'} 5s ease-in-out infinite;
  width: fit-content;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// Expert Image - Responsive
const ExpertImage = styled.img<{ $category: string; $vh: number }>`
  width: auto;
  height: auto;
  max-width: ${props => {
    switch(props.$category) {
      case 'tv': return '700px';
      case 'desktop': return '500px';
      case 'tablet': return '400px';
      case 'mobile': return '90%';
      default: return '85%';
    }
  }};
  max-height: ${props => {
    switch(props.$category) {
      case 'mobile': return `${Math.min(props.$vh * 0.3, 250)}px`;
      case 'mobile-small': return `${Math.min(props.$vh * 0.25, 200)}px`;
      default: return `${Math.min(props.$vh * 0.4, 500)}px`;
    }
  }};
  object-fit: contain;
  border-radius: 16px;
  border: 3px solid rgba(255, 215, 0, 0.4);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3), 0 0 40px rgba(255, 215, 0, 0.2);
  cursor: default;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
`;

// As Seen On Badge - positioned on image corner
const AsSeenOnBadge = styled.div<{ $category: string }>`
  position: absolute;
  top: 10px;
  right: 10px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff4444 100%);
  color: white;
  padding: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '6px 12px' : '8px 16px'};
  border-radius: 30px;
  font-size: ${props => {
    switch(props.$category) {
      case 'tv': return '18px';
      case 'desktop': return '14px';
      case 'tablet': return '13px';
      case 'mobile': return '11px';
      default: return '10px';
    }
  }};
  font-weight: 700;
  box-shadow: 0 10px 25px rgba(255, 68, 68, 0.4);
  z-index: 2;
  transform: rotate(0deg);
`;

// CTA Button Primary
const CTAButtonPrimary = styled.button<{ $category: string }>`
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #1a1a1a;
  border: 2px solid #FFD700;
  border-radius: 3px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: ${props => props.$category === 'mobile-small' ? '0.5px' : '1px'};
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex-shrink: 0;
  padding: ${props => {
    switch(props.$category) {
      case 'tv': return '22px 50px';
      case 'desktop': return '18px 40px';
      case 'tablet': return '15px 30px';
      case 'mobile': return '16px 32px';
      default: return '14px 28px';
    }
  }};
  font-size: ${props => {
    switch(props.$category) {
      case 'tv': return '22px';
      case 'desktop': return '18px';
      case 'tablet': return '16px';
      case 'mobile': return '16px';
      default: return '14px';
    }
  }};
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    transform: translate(-50%, -50%);
    transition: width 0.5s, height 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 215, 0, 0.4);
    background: linear-gradient(135deg, #FFA500 0%, #FFD700 100%);
    
    &::before {
      width: 400px;
      height: 400px;
    }
  }
`;

const CTAButtonSecondary = styled(CTAButtonPrimary)`
  background: transparent;
  border: 2px solid #10a37f;
  color: #10a37f;
  box-shadow: 0 4px 12px rgba(16, 163, 127, 0.2);
  
  &:hover {
    background: rgba(16, 163, 127, 0.1);
    box-shadow: 0 6px 16px rgba(16, 163, 127, 0.3);
  }
`;

// Slider Controls
const SliderControls = styled.div<{ $category: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '10px 0' : '15px 0'};
  flex-shrink: 0;
  margin-top: auto;
`;

// Arrow Buttons - Enhanced visibility without slider box
const SliderArrowButton = styled.button<{ direction: 'prev' | 'next'; $category: string }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'prev' ? 'left: 20px;' : 'right: 20px;'}
  width: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '40px' : '50px'};
  height: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '40px' : '50px'};
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.8);
  border: 3px solid rgba(255, 215, 0, 0.9);
  color: #ffd700;
  font-size: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '20px' : '24px'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(15px);
  font-weight: 900;
  z-index: 10;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
  
  &:hover {
    background: rgba(255, 215, 0, 0.3);
    transform: translateY(-50%) scale(1.15);
    border-color: #ffd700;
    box-shadow: 0 0 30px rgba(255, 215, 0, 0.6);
  }
`;

// Auto-play indicator - subtle and non-distracting
const AutoPlayBadge = styled.div<{ $category: string }>`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.4);
  color: rgba(255, 255, 255, 0.6);
  padding: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '3px 6px' : '4px 8px'};
  border-radius: 15px;
  font-size: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '9px' : '10px'};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;
  opacity: 0.7;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 1;
  }
  
  button {
    background: transparent;
    border: none;
    color: #ffd700;
    cursor: pointer;
    font-size: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '12px' : '14px'};
    padding: 0;
    
    &:hover {
      transform: scale(1.2);
    }
  }
`;

// Dots Navigation
const DotsWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Dot = styled.button<{ $active: boolean; $category: string }>`
  width: ${props => props.$active ? (props.$category === 'mobile-small' ? '25px' : '35px') : '12px'};
  height: ${props => props.$category === 'mobile-small' ? '10px' : '12px'};
  border-radius: 6px;
  border: none;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #ffd700, #ffa500)' 
    : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: ${props => props.$active ? '0 4px 15px rgba(255, 215, 0, 0.4)' : 'none'};
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, #ffd700, #ffa500)' 
      : 'rgba(255, 255, 255, 0.5)'};
    transform: scale(1.1);
  }
`;

const ButtonGroup = styled.div<{ $category: string }>`
  display: flex;
  gap: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '10px' : '15px'};
  justify-content: center;
  flex-wrap: ${props => props.$category === 'mobile-small' ? 'wrap' : 'nowrap'};
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  
  ${props => (props.$category === 'mobile-small') && `
    flex-direction: column;
    
    button {
      width: 100%;
      max-width: 300px;
    }
  `}
  
  ${props => (props.$category === 'mobile') && `
    flex-direction: row;
    
    button {
      flex: 1;
      max-width: 180px;
    }
  `}
`;

// Tool Cards for Slide 4
const ToolCard = styled.div<{ $category: string; $color: 'gold' | 'green' }>`
  background: linear-gradient(135deg, 
    ${props => props.$color === 'gold' 
      ? 'rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.08) 100%' 
      : 'rgba(16, 163, 127, 0.15) 0%, rgba(16, 163, 127, 0.08) 100%'
    }
  );
  border: 3px solid ${props => props.$color === 'gold' ? 'rgba(255, 215, 0, 0.4)' : 'rgba(16, 163, 127, 0.4)'};
  border-radius: 20px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  width: 100%;
  padding: ${props => {
    switch(props.$category) {
      case 'tv': return '35px';
      case 'desktop': return '25px';
      case 'tablet': return '20px';
      case 'mobile': return '15px';
      default: return '12px';
    }
  }};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px ${props => props.$color === 'gold' 
      ? 'rgba(255, 215, 0, 0.3)' 
      : 'rgba(16, 163, 127, 0.3)'
    };
  }
  
  .icon {
    font-size: ${props => {
      switch(props.$category) {
        case 'tv': return '64px';
        case 'desktop': return '48px';
        case 'tablet': return '40px';
        case 'mobile': return '32px';
        default: return '28px';
      }
    }};
    margin-bottom: 10px;
  }
  
  h3 {
    font-size: ${props => {
      switch(props.$category) {
        case 'tv': return '28px';
        case 'desktop': return '22px';
        case 'tablet': return '20px';
        case 'mobile': return '18px';
        default: return '16px';
      }
    }};
    font-weight: 900;
    color: ${props => props.$color === 'gold' ? '#FFD700' : '#10a37f'};
    margin: 10px 0;
  }
  
  p {
    font-size: ${props => {
      switch(props.$category) {
        case 'tv': return '18px';
        case 'desktop': return '15px';
        case 'tablet': return '14px';
        case 'mobile': return '13px';
        default: return '12px';
      }
    }};
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
  }
`;

const ToolsContainer = styled.div<{ $category: string }>`
  display: flex;
  flex-direction: ${props => props.$category === 'mobile-small' ? 'column' : 'row'};
  gap: ${props => props.$category === 'mobile-small' || props.$category === 'mobile' ? '15px' : '20px'};
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  justify-content: center;
`;

const ToolCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 15px;
  align-items: center;
`;

// Mobile-optimized content wrapper
const MobileContentWrapper = styled.div<{ $category: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1 1 auto;
  gap: ${props => props.$category === 'mobile-small' ? '10px' : '12px'};
  padding: ${props => props.$category === 'mobile-small' ? '8px 0' : '10px 0'};
  overflow-y: auto;
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 215, 0, 0.3);
    border-radius: 2px;
  }
`;

// Mobile-optimized card for better viewport usage
const MobileCard = styled.div<{ $category: string; $variant?: 'primary' | 'secondary' }>`
  background: ${props => props.$variant === 'secondary' 
    ? 'linear-gradient(135deg, rgba(16, 163, 127, 0.15) 0%, rgba(16, 163, 127, 0.08) 100%)'
    : 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.08) 100%)'
  };
  border: 2px solid ${props => props.$variant === 'secondary'
    ? 'rgba(16, 163, 127, 0.4)'
    : 'rgba(255, 215, 0, 0.4)'
  };
  border-radius: 16px;
  padding: ${props => props.$category === 'mobile-small' ? '15px' : '20px'};
  width: 100%;
  max-width: 350px;
  text-align: center;
  flex-shrink: 0;
  
  h3 {
    font-size: ${props => props.$category === 'mobile-small' ? '18px' : '20px'};
    color: ${props => props.$variant === 'secondary' ? '#10a37f' : '#ffd700'};
    margin: 0 0 8px 0;
    font-weight: 800;
  }
  
  p {
    font-size: ${props => props.$category === 'mobile-small' ? '13px' : '14px'};
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    line-height: 1.4;
  }
`;

// Modal styles (simplified)
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(10px);
`;

interface HeroSectionUltimateProps {}

export const HeroSectionUltimate: React.FC<HeroSectionUltimateProps> = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [videoModalUrl, setVideoModalUrl] = useState<string>('');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [viewport, setViewport] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
    category: getViewportCategory(window.innerWidth, window.innerHeight)
  });
  const { openCalendly } = useCalendlyPopup();
  
  // Always show shocking fact as separate slide
  const shouldSeparateShockingFact = true;
  const totalSlides = 5;
  
  useEffect(() => {
    let resizeTimer: NodeJS.Timeout;
    
    const handleResize = () => {
      // Use visualViewport when available for mobile accuracy
      const vh = window.visualViewport?.height || window.innerHeight;
      const vw = window.visualViewport?.width || window.innerWidth;
      const category = getViewportCategory(vw, vh);
      
      // Debounce viewport updates to avoid jarring transitions
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setViewport({ height: vh, width: vw, category });
        
        // Set CSS variables for fluid units
        document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
        document.documentElement.style.setProperty('--vw', `${vw * 0.01}px`);
      }, 100);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
    }
    
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      }
    };
  }, []);
  
  // Automatic scrolling disabled per user request
  // Users can manually navigate using the dots/indicators
  useEffect(() => {
    // Timer disabled - manual navigation only
    return () => {};
  }, [currentSlide, isAutoPlaying, totalSlides]);
  
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };
  
  const navigate = (direction: 'prev' | 'next') => {
    setIsAutoPlaying(false);
    if (direction === 'prev') {
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    } else {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }
  };
  
  // Smart conditional rendering based on available viewport space  
  const shouldShowToolCards = (viewport.category === 'desktop' || viewport.category === 'tv') || 
    (viewport.category === 'tablet' && viewport.height > 700);
  
  const shouldShowFullQuestion = viewport.category !== 'mobile-small' && 
    (viewport.height > 500 || viewport.category === 'desktop' || viewport.category === 'tv');
  
  const handleVideoClick = (url: string) => {
    setVideoModalUrl(url);
    setIsVideoModalOpen(true);
  };
  
  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    setVideoModalUrl('');
  };
  
  return (
    <>
      <GlobalViewportStyles />
      <HeroWrapper $vh={viewport.height}>
        <HeroContent $category={viewport.category}>
          {/* Shocking fact is now always in its own slide */}
          
          <SliderContainer $category={viewport.category}>
            {isAutoPlaying && (
              <AutoPlayBadge $category={viewport.category}>
                <span>Auto</span>
                <button onClick={() => setIsAutoPlaying(false)} style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: 'inherit', 
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 'inherit'
                }}>
                  ⏸
                </button>
              </AutoPlayBadge>
            )}
            
            <SliderArrowButton 
              direction="prev" 
              onClick={() => navigate('prev')} 
              aria-label="Previous slide"
              $category={viewport.category}
            >
              ←
            </SliderArrowButton>
            
            <SliderArrowButton 
              direction="next" 
              onClick={() => navigate('next')} 
              aria-label="Next slide"
              $category={viewport.category}
            >
              →
            </SliderArrowButton>
            
            <SliderTrack $currentSlide={currentSlide}>
              {/* Slide 0: The Quintessential Question - ALWAYS FIRST */}
              <Slide $category={viewport.category}>
                {viewport.category === 'mobile' || viewport.category === 'mobile-small' ? (
                  <>
                    <div style={{ flexShrink: 0, marginBottom: '15px' }}>
                      <SlideTitle $category={viewport.category} style={{ marginBottom: '12px' }}>
                        The <span className="gold">Quintessential Question</span>
                      </SlideTitle>
                    </div>
                    
                    <MobileContentWrapper $category={viewport.category} style={{ 
                      flex: '1 1 auto',
                      display: 'flex',
                      alignItems: 'stretch',
                      padding: '0'
                    }}>
                      <QuestionCard 
                        onClick={() => window.location.href = '/quintessential-question'}
                        $category={viewport.category}
                        style={{ 
                          maxWidth: '100%', 
                          margin: '0 auto',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}
                      >
                        <div>
                          <h3 style={{ marginBottom: '15px' }}>Every Annuity Buyer Should Ask:</h3>
                          <p style={{ 
                            fontStyle: 'italic', 
                            marginBottom: '15px',
                            fontSize: viewport.category === 'mobile-small' ? '14px' : '16px', // Increased for better readability
                            lineHeight: '1.7'
                          }}>
                            {/* IMPORTANT: Do NOT change the wording of this question - only truncate if needed to fit viewport */}
                            {(() => {
                              const fullQuestion = "What assumptions go into this annuity illustration, and how might they affect the expectations I have compared to actual results? Are the returns shown based on real historical index performance, or on backfilled simulations created after the index was launched? When reviewing backtesting results, how can I know whether the process avoids common distortions—such as cherry-picking dates, using backfilled data, or overlooking changes in rates over time—and instead relies on transparent, consistent methods that give me a fair picture of long-term performance?";
                              
                              // Calculate approximate max characters based on viewport
                              let maxChars;
                              if (viewport.category === 'mobile-small') {
                                // Smaller screens with bigger text = less characters
                                maxChars = Math.min(280, fullQuestion.length);
                              } else {
                                // Regular mobile with bigger text
                                maxChars = Math.min(350, fullQuestion.length);
                              }
                              
                              // Adjust based on viewport height
                              if (viewport.height < 700) {
                                maxChars = Math.floor(maxChars * 0.8);
                              } else if (viewport.height < 600) {
                                maxChars = Math.floor(maxChars * 0.6);
                              }
                              
                              if (fullQuestion.length <= maxChars) {
                                return `"${fullQuestion}"`;
                              }
                              
                              // Truncate at word boundary
                              let truncated = fullQuestion.substring(0, maxChars);
                              const lastSpace = truncated.lastIndexOf(' ');
                              if (lastSpace > 0) {
                                truncated = truncated.substring(0, lastSpace);
                              }
                              
                              return `"${truncated}..."`;
                            })()}
                          </p>
                        </div>
                        <p style={{ 
                          color: '#FFD700',
                          fontWeight: '700',
                          textAlign: 'center',
                          textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)',
                          fontSize: viewport.category === 'mobile-small' ? '13px' : '14px',
                          marginTop: 'auto',
                          paddingTop: '10px'
                        }}>
                          Click to view full question & answer →
                        </p>
                      </QuestionCard>
                    </MobileContentWrapper>
                    
                    <CTAButtonPrimary 
                      onClick={() => window.location.href = '/quintessential-question'}
                      $category={viewport.category}
                      style={{ marginTop: '10px', marginBottom: '70px' }}
                    >
                      READ FULL QUESTION & ANSWER
                    </CTAButtonPrimary>
                  </>
                ) : (
                  <>
                    <SlideTitle $category={viewport.category}>
                      The <span className="gold">Quintessential Question</span>
                    </SlideTitle>
                    
                    <QuestionCard 
                      onClick={() => window.location.href = '/quintessential-question'}
                      $category={viewport.category}
                    >
                      <h3>Every Annuity Buyer Should Ask:</h3>
                      <p style={{ fontStyle: 'italic', marginBottom: '15px' }}>
                        {/* IMPORTANT: Do NOT change the wording of this question - only truncate if needed to fit viewport */}
                        {(() => {
                          const fullQuestion = "What assumptions go into this annuity illustration, and how might they affect the expectations I have compared to actual results? Are the returns shown based on real historical index performance, or on backfilled simulations created after the index was launched? When reviewing backtesting results, how can I know whether the process avoids common distortions—such as cherry-picking dates, using backfilled data, or overlooking changes in rates over time—and instead relies on transparent, consistent methods that give me a fair picture of long-term performance?";
                          
                          // Determine how much text to show based on viewport
                          let maxLength;
                          switch(viewport.category) {
                            case 'tv':
                              maxLength = fullQuestion.length; // Show all on TV
                              break;
                            case 'desktop':
                              maxLength = fullQuestion.length; // Show all on desktop
                              break;
                            case 'tablet':
                              maxLength = 250; // Show moderate portion on tablet
                              break;
                            default:
                              maxLength = 150;
                          }
                          
                          // Truncate at word boundary
                          if (fullQuestion.length <= maxLength) {
                            return `"${fullQuestion}"`;
                          }
                          
                          let truncated = fullQuestion.substring(0, maxLength);
                          const lastSpace = truncated.lastIndexOf(' ');
                          if (lastSpace > 0) {
                            truncated = truncated.substring(0, lastSpace);
                          }
                          
                          return `"${truncated}..."`;
                        })()}
                      </p>
                      <p style={{ 
                        color: '#FFD700',
                        fontWeight: '700',
                        textAlign: 'center',
                        textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)'
                      }}>
                        Click to view full question & answer →
                      </p>
                    </QuestionCard>
                    
                    <ButtonGroup $category={viewport.category}>
                      <CTAButtonPrimary 
                        onClick={() => window.location.href = '/quintessential-question'}
                        $category={viewport.category}
                      >
                        Read Full Question & Answer
                      </CTAButtonPrimary>
                    </ButtonGroup>
                  </>
                )}
              </Slide>
              
              {/* Slide 1: Testimonials & Trust */}
              <Slide $category={viewport.category}>
                <SlideTitle $category={viewport.category}>
                  Happy Clients <span className="gold">Tell The Story</span>
                </SlideTitle>
                <SlideSubtitle $category={viewport.category}>
                  40 years. Blemish free record.
                </SlideSubtitle>
                
                {viewport.category === 'mobile' || viewport.category === 'mobile-small' || viewport.category === 'tablet' ? (
                  <VideoTestimonialCarousel onTestimonialClick={handleVideoClick} />
                ) : (
                  <VideoTestimonialCarousel displayMode="grid" onTestimonialClick={handleVideoClick} />
                )}
                
                <CTAButtonPrimary 
                  onClick={() => window.location.href = '/testimonials'}
                  $category={viewport.category}
                  style={{
                    marginBottom: viewport.category === 'mobile' || viewport.category === 'mobile-small' ? '70px' : '0'
                  }}
                >
                  View Testimonials
                </CTAButtonPrimary>
              </Slide>
              
              {/* Slide 2: Meet the Experts */}
              <Slide $category={viewport.category}>
                {viewport.category === 'mobile' || viewport.category === 'mobile-small' ? (
                  <>
                    <div style={{ flexShrink: 0 }}>
                      <SlideTitle $category={viewport.category} style={{ marginBottom: '15px' }}>
                        Your <span className="gold">Trusted Experts</span>
                      </SlideTitle>
                      <SlideSubtitle $category={viewport.category}>
                        Tom Hegna & Cap Averill II – America's Leading Annuity Experts
                      </SlideSubtitle>
                    </div>
                    
                    <MobileContentWrapper $category={viewport.category}>
                      <ExpertImageContainer $category={viewport.category}>
                        {/* <AsSeenOnBadge $category={viewport.category}>As Seen on CNBC</AsSeenOnBadge> */}
                        <ExpertImage 
                          src="/hegnacap-for-site.png"
                          alt="Tom Hegna & Cap Averill II – America's Leading Annuity Experts"
                          $category={viewport.category}
                          $vh={viewport.height}
                        />
                      </ExpertImageContainer>
                    </MobileContentWrapper>
                    
                    <CTAButtonPrimary 
                      onClick={() => setIsPhoneModalOpen(true)}
                      $category={viewport.category}
                      style={{ marginBottom: '70px' }}
                    >
                      TALK WITH CAP
                    </CTAButtonPrimary>
                  </>
                ) : (
                  <>
                    <SlideTitle $category={viewport.category}>
                      Your <span className="gold">Trusted Experts</span>
                    </SlideTitle>
                    
                    <ExpertImageContainer $category={viewport.category}>
                      {/* <AsSeenOnBadge $category={viewport.category}>As Seen on CNBC</AsSeenOnBadge> */}
                      <ExpertImage 
                        src="/hegnacap-for-site.png"
                        alt="Tom Hegna & Cap Averill II – America's Leading Annuity Experts"
                        $category={viewport.category}
                        $vh={viewport.height}
                      />
                    </ExpertImageContainer>
                    
                    <SlideSubtitle $category={viewport.category}>
                      Tom Hegna & Cap Averill II – America's Leading Annuity Experts
                    </SlideSubtitle>
                    
                    <CTAButtonPrimary 
                      onClick={() => setIsPhoneModalOpen(true)}
                      $category={viewport.category}
                    >
                      Talk With Cap
                    </CTAButtonPrimary>
                  </>
                )}
              </Slide>
              
              {/* Slide 3: Free Tools */}
              <Slide $category={viewport.category} style={{
                justifyContent: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px',
                  width: '100%',
                  paddingBottom: viewport.category === 'mobile' || viewport.category === 'mobile-small' ? '70px' : '0'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <SlideTitle $category={viewport.category} style={{ marginBottom: '15px' }}>
                      <span className="gold">Professional Annuity Analysis</span>
                    </SlideTitle>
                    <SlideSubtitle $category={viewport.category}>
                      <span style={{ fontSize: '14px', display: 'block', marginBottom: '15px' }}>
                        The biggest decision in your life: the annuity vehicle you'll depend on for income for yourself, your spouse, and your family for the rest of your life.
                      </span>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: '#ffd700', marginTop: '15px', display: 'block' }}>
                        Isn't it worth knowing if you could be doing significantly better?
                      </span>
                    </SlideSubtitle>
                  </div>
                  
                  {shouldShowToolCards ? (
                    <ToolsContainer $category={viewport.category}>
                      <ToolCardWrapper>
                        <ToolCard 
                          $category={viewport.category} 
                          $color="gold"
                          onClick={() => window.location.href = '/quick-answer'}
                        >
                          <div className="icon">⚡</div>
                          <h3>Quick Answer</h3>
                          <p>Get instant expert feedback</p>
                        </ToolCard>
                        {(viewport.category === 'desktop' || viewport.category === 'tv') && (
                          <CTAButtonPrimary 
                            onClick={() => window.location.href = '/quick-answer'}
                            $category={viewport.category}
                            style={{ width: '100%', maxWidth: '250px' }}
                          >
                            GET QUICK ANSWER
                          </CTAButtonPrimary>
                        )}
                      </ToolCardWrapper>
                      
                      <ToolCardWrapper>
                        <ToolCard 
                          $category={viewport.category} 
                          $color="green"
                          onClick={() => window.location.href = '/deep-dive'}
                        >
                          <div className="icon">📊</div>
                          <h3>Deep Dive</h3>
                          <p>Comprehensive portfolio review</p>
                        </ToolCard>
                        {(viewport.category === 'desktop' || viewport.category === 'tv') && (
                          <CTAButtonSecondary 
                            onClick={() => window.location.href = '/deep-dive'}
                            $category={viewport.category}
                            style={{ width: '100%', maxWidth: '250px' }}
                          >
                            GET DEEP DIVE
                          </CTAButtonSecondary>
                        )}
                      </ToolCardWrapper>
                    </ToolsContainer>
                  ) : (
                    <ButtonGroup $category={viewport.category}>
                      <CTAButtonPrimary 
                        onClick={() => window.location.href = '/quick-answer'}
                        $category={viewport.category}
                      >
                        GET QUICK ANSWER
                      </CTAButtonPrimary>
                      <CTAButtonSecondary 
                        onClick={() => window.location.href = '/deep-dive'}
                        $category={viewport.category}
                      >
                        GET DEEP DIVE
                      </CTAButtonSecondary>
                    </ButtonGroup>
                  )}
                </div>
              </Slide>
              
              {/* Slide 4: Shocking Fact - Always shown as separate slide */}
              <Slide $category={viewport.category}>
                {viewport.category === 'mobile' || viewport.category === 'mobile-small' ? (
                  <>
                    <div style={{ flexShrink: 0 }}>
                      <PremiumAlert $category={viewport.category} $vh={viewport.height}>
                        <strong>📊 SHOCKING FACT:</strong> While the <strong style={{ color: '#10a37f' }}>S&P 500 soared 24%</strong>,
                        popular annuity indices averaged just <strong style={{ color: '#ff6b6b' }}>3%</strong> — Don't be fooled!
                      </PremiumAlert>
                    </div>
                    
                    <MobileContentWrapper $category={viewport.category}>
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(255, 68, 68, 0.15) 0%, rgba(255, 68, 68, 0.08) 100%)',
                        border: '2px solid rgba(255, 68, 68, 0.4)',
                        borderRadius: '16px',
                        padding: '20px',
                        width: '100%',
                        maxWidth: '350px',
                        textAlign: 'center'
                      }}>
                        <h3 style={{ 
                          fontSize: viewport.category === 'mobile-small' ? '20px' : '24px',
                          color: '#ffd700',
                          marginBottom: '12px',
                          fontWeight: '900'
                        }}>
                          The S&P 500 soared 24%
                        </h3>
                        <p style={{
                          fontSize: viewport.category === 'mobile-small' ? '14px' : '16px',
                          color: 'white',
                          marginBottom: '12px',
                          lineHeight: '1.5'
                        }}>
                          While popular annuity indices averaged just <strong style={{ color: '#ff6b6b', fontSize: '120%' }}>3%</strong>
                        </p>
                        <p style={{
                          fontSize: viewport.category === 'mobile-small' ? '18px' : '20px',
                          color: '#ffd700',
                          fontWeight: '800',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          textShadow: '0 2px 10px rgba(255, 215, 0, 0.4)'
                        }}>
                          Don't be fooled!
                        </p>
                      </div>
                    </MobileContentWrapper>
                    
                    <CTAButtonPrimary 
                      onClick={() => {
                        const truthSection = document.getElementById('truth-section');
                        if (truthSection) {
                          truthSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      $category={viewport.category}
                      style={{ marginBottom: '70px' }}
                    >
                      LEARN THE TRUTH
                    </CTAButtonPrimary>
                  </>
                ) : (
                  <>
                    <SlideTitle $category={viewport.category}>
                      <span className="gold">📊 SHOCKING FACT</span>
                    </SlideTitle>
                    <SlideSubtitle $category={viewport.category} style={{ fontSize: viewport.category === 'desktop' ? 'clamp(22px, 2vw, 28px)' : undefined, maxWidth: '800px' }}>
                      While the <strong style={{ color: '#10a37f' }}>S&P 500 soared 24%</strong>,<br/>
                      popular annuity indices averaged just <strong style={{ color: '#ff6b6b' }}>3%</strong>
                    </SlideSubtitle>
                    <SlideSubtitle $category={viewport.category} style={{ marginTop: '20px', fontSize: 'clamp(18px, 1.8vw, 24px)', color: '#ffd700' }}>
                      Don't be fooled!
                    </SlideSubtitle>
                    <CTAButtonPrimary 
                      onClick={() => {
                        const truthSection = document.getElementById('truth-section');
                        if (truthSection) {
                          truthSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      $category={viewport.category}
                    >
                      Learn The Truth
                    </CTAButtonPrimary>
                  </>
                )}
              </Slide>
            </SliderTrack>
          </SliderContainer>
          
          <SliderControls $category={viewport.category}>
            <DotsWrapper>
              {[...Array(totalSlides)].map((_, index) => (
                <Dot
                  key={index}
                  $active={currentSlide === index}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  $category={viewport.category}
                />
              ))}
            </DotsWrapper>
          </SliderControls>
        </HeroContent>
      </HeroWrapper>
      
      <VideoModal 
        isOpen={isVideoModalOpen}
        videoUrl={videoModalUrl}
        onClose={handleCloseVideoModal}
        title="Client Testimonial"
      />
      
      <PhoneModal 
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
        openCalendly={openCalendly}
      />
    </>
  );
};